<script setup lang="ts">
import { computed, ref } from "vue";
import { useAppMode } from "@/composables/useAppMode";
import PreviewSeat from "@/components/table/seat/PreviewSeat.vue";
import PlayingCard from "@/components/table/PlayingCard.vue";
import PreviewChipStack from "@/components/table/chips/PreviewChipStack.vue";
import Typography from "@/components/ui/typography/Typography.vue";
import PlayingCardUnknown from "@/components/table/PlayingCardUnknown.vue";

import dealerImage from "@/assets/dealer.png";

import {
  seatConfigByCount,
  dealerChipLocationsByCount,
  chipStackLocationsByCount,
} from "@/constants";
import { stringifyGamingConfiguration } from "@/helpers/stringify-gaming-configuration";

import { Street } from "@acepoker/contract";
import type { PreviewTablePlayer } from "@acepoker/games-nlhe/frontend/types";
import type { Drill } from "@acepoker/contract";

const props = withDefaults(
  defineProps<{
    gameConfig?: Pick<
      Drill,
      "gaming_configuration" | "max_players"
    >;
    seats?: PreviewTablePlayer[];
    street?: Street;
    tableCards?: string[];
    disabled?: boolean;
  }>(),
  {
    seats: () => [],
    tableCards: () => [],
  },
);

const disabledState = computed(() => !props.seats.length || props.disabled);

const { mode } = useAppMode();
const seatCount = computed(() => props.gameConfig?.max_players ?? 8);
const seatConfig = computed(
  () => seatConfigByCount[mode.value][seatCount.value],
);
const dealerChipLocations = computed(
  () => dealerChipLocationsByCount[mode.value][seatCount.value],
);
const chipCountsLocations = computed(
  () => chipStackLocationsByCount[mode.value][seatCount.value],
);

const tableRotation = ref(0);
// TODO ???
// const tableRotation = computed(() => {
//   if (!isGameStarted.value) {
//     return 0
//   }

//   const myIndex = player.value
//     ? roomState.value.seats.findIndex((seat) => seat?.playerId === player.value!.playerId)
//     : -1

//   if (myIndex === -1) {
//     return 0
//   }

//   return -myIndex
// })
const index = (idx: number) =>
  (idx + tableRotation.value + seatCount.value) % seatCount.value;

const dealerIndex = computed(
  () => props.seats.find((el) => el?.position === "BTN")?.seat_no,
);

const tableSettings = computed(() =>
  props.gameConfig
    ? stringifyGamingConfiguration(
        props.gameConfig?.gaming_configuration,
        seatCount.value,
      )
    : "",
);

const communityCardsCount = computed(() => {
  switch (props.street) {
    case Street.Flop:
      return 3;
    case Street.Turn:
      return 4;
    case Street.River:
      return 5;
    default:
      return 0;
  }
});
const communityCards = computed(() =>
  props.tableCards.length
    ? props.tableCards
    : Array(communityCardsCount.value).fill(null),
);
</script>

<template>
  <div
    class="table-preview table"
    :class="{
      'table--desktop': mode === 'desktop',
      'table--mobile': mode === 'mobile',
      'table-preview--disabled': disabledState,
    }"
  >
    <div :class="`table__game-zone table__game-zone--${mode}`">
      <img
        v-if="!disabledState"
        :src="dealerImage"
        class="table__button"
        :class="{
          [`table__button--${mode}`]: true,
        }"
        :style="{
          left: `${dealerChipLocations[index(dealerIndex)]?.left}%`,
          top: `${dealerChipLocations[index(dealerIndex)]?.top}%`,
        }"
      />

      <Typography
        size="text-12-18"
        class="opacity-14 absolute left-[50%] top-[44%] mt-[5px] -translate-x-1/2 whitespace-nowrap text-center lg:top-[75%]"
        >{{ tableSettings }}</Typography
      >

      <div
        class="table__community-cards"
        :class="`table__community-cards--${mode}`"
      >
        <div class="table__street-container">
          <div class="table__community-card-wrapper" v-for="() in 5"></div>
          <div v-if="communityCardsCount" class="table__community-cards-inner">
            <template v-for="(card, idx) in communityCards">
              <playing-card
                v-if="card"
                class="table__community-card"
                :value="card"
              />
              <playing-card-unknown v-else class="table__community-card" />
            </template>
          </div>
        </div>
      </div>

      <template
        v-for="(player, idx) in props.seats.slice(0, seatCount)"
        :key="player.seat_no"
      >
        <preview-chip-stack
          v-if="player.chips"
          :style="{
            left: `${chipCountsLocations[index(player.seat_no)].left}%`,
            top: `${chipCountsLocations[index(player.seat_no)].top}%`,
          }"
          class="table__stack"
          :class="{
            [`table__stack--${mode}`]: true,
            // 'table__stack--self':
            //   roomState.seats[idx]?.playerId === player?.playerId,
          }"
          :value="player.chips"
        />
        <PreviewSeat
          v-if="player"
          :key="player.playerId"
          :index="index(player.seat_no)"
          :position="player.position"
          :seat="player"
          :mode="seatConfig[index(player.seat_no)].mode"
          :style="{
            left: `${seatConfig[index(player.seat_no)].left}%`,
            top: `${seatConfig[index(player.seat_no)].top}%`,
          }"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.table--desktop {
  --table-size: 40;

  // TODO: review origin table margins
  margin-top: calc(var(--th) * 15);
  margin-bottom: calc(var(--th) * 10);
  margin-left: calc(var(--tw) * 7);
  margin-right: calc(var(--tw) * 7);
}
@media (min-width: 1200px) {
  .table--desktop {
    --table-size: 45;
  }
}
</style>

<style lang="scss">
.table-preview--disabled {
  pointer-events: none;
  opacity: 0.5;
}

.preview-seat__placeholder {
  width: 100%;
  border-radius: 8px;
  font-weight: 500;
  word-wrap: break-word;
  color: #e4e4e499;
  text-align: center;
  white-space: nowrap;
}

.table__stack--highlight-position {
  .seat-plate__content .preview-seat__placeholder {
    background-color: greenyellow;
  }
  .seat-plate__content--mobile .preview-seat__placeholder {
    background-color: rgb(62, 101, 2);
  }
}

.table__street-container {
  width: calc(var(--card-table-width) * 5 + var(--tw) * 0.7 * 4);

  @for $i from 1 to 10 {
    .table__community-card:nth-child(#{$i}) {
      position: absolute;
      left: calc(
        var(--card-table-width) * ($i - 1) + var(--tw) * 0.7 * ($i - 1)
      );
      top: 0;
    }
  }
}

.table__community-card-wrapper:nth-of-type(4) {
  margin-left: calc(var(--card-table-width) * 3 + var(--tw) * 0.7 * 3);
}
.table__community-card-wrapper:nth-of-type(5) {
  margin-left: calc(var(--card-table-width) * 4 + var(--tw) * 0.7 * 4);
}

// hack to match with old chips positions
.table-preview .table__stack--mobile {
  margin-left: calc(var(--tw) * 2.5);
}
.table-preview .table__stack--desktop {
  margin-left: calc(var(--tw) * -1.2);
}
</style>
